// v2/components/admin/InscripcionesPorPersona.jsx
//
// Resuelve el bug O11 reportado por la coordinadora:
// la ficha de un familiar (Fresia) mostraba "Sin inscripciones" porque
// la query usaba `usuario: cognitoId`. Este componente busca por RUT o
// familiarId y devuelve TODAS las inscripciones, sin importar quién las
// haya comprado (titular o familiar).
//
// Se muestra como lista de tarjetas y no como tabla: vive dentro de la ficha
// (un drawer angosto) y ocho columnas no cabían.

import React, { useEffect, useState, useCallback } from 'react';
import { Input, Spin, Alert, Tooltip, Pagination } from 'antd';
import { FiClock, FiSearch, FiUser, FiCalendar, FiShoppingBag, FiRepeat } from 'react-icons/fi';
import dayjs from 'dayjs';
import AdminV2 from '../../../../service/admin.service';
import { mensajeDeError } from '../../../../service/client';
import { formatearNombre } from '../../../../utils/texto';
import { SALDO, textoSaldo, pesos } from '../../../../utils/saldoCambioTaller';

const POR_PAGINA = 8;

const ESTADOS = {
  vigente:   { label: 'Vigente',   cls: 'bg-blue-50 text-primary' },
  vencida:   { label: 'Vencida',   cls: 'bg-gray-100 text-gray-500' },
  pendiente: { label: 'Pendiente', cls: 'bg-secondary/40 text-[#5c4300]' },
  cancelada: { label: 'Cancelada', cls: 'bg-red-50 text-red-700' },
};

// "19.114.278-0" y "19114278-0" son el mismo RUT.
const rutLimpio = (r) => (r || '').replace(/[.\-\s]/g, '').toLowerCase();

const estadoDe = (row) => {
  if (row.estado === 'confirmada') return row.vigente ? ESTADOS.vigente : ESTADOS.vencida;
  if (row.estado === 'cancelada') return ESTADOS.cancelada;
  if (row.estado === 'pendiente') return ESTADOS.pendiente;
  return { label: row.estado || '—', cls: 'bg-gray-100 text-gray-600' };
};

/**
 * @param {object} props
 * @param {string} [props.rut] - si se pasa, busca automáticamente
 * @param {string} [props.familiarId]
 * @param {string} [props.email]
 * @param {string} [props.usuarioId]
 */
export default function InscripcionesPorPersona({ rut: rutInicial, familiarId, email, usuarioId }) {
  const [rutBusqueda, setRutBusqueda] = useState(rutInicial || '');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [buscado, setBuscado] = useState(false);
  const [pagina, setPagina] = useState(1);

  const buscar = useCallback(async (filtros) => {
    if (!filtros.rut && !filtros.familiarId && !filtros.email && !filtros.usuarioId) return;
    setLoading(true);
    setError(null);
    try {
      const out = await AdminV2.inscripcionesPorPersona(filtros);
      setItems(out.inscripciones || []);
      setBuscado(true);
      setPagina(1);
    } catch (err) {
      setError(mensajeDeError(err, 'No se pudieron cargar las inscripciones'));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (rutInicial || familiarId || email || usuarioId) {
      buscar({ rut: rutInicial, familiarId, email, usuarioId });
    }
  }, [rutInicial, familiarId, email, usuarioId, buscar]);

  const conFiltroPorProps = rutInicial || familiarId || email || usuarioId;
  const visibles = items.slice((pagina - 1) * POR_PAGINA, pagina * POR_PAGINA);
  const nVigentes = items.filter(i => i.estado === 'confirmada' && i.vigente).length;

  return (
    <div>
      {!conFiltroPorProps && (
        <Input
          allowClear prefix={<FiSearch className="text-gray-400" />}
          placeholder="Buscar por RUT (12345678-9)"
          value={rutBusqueda}
          onChange={e => setRutBusqueda(e.target.value)}
          onPressEnter={() => rutBusqueda?.trim() && buscar({ rut: rutBusqueda.trim() })}
          className="!rounded-full mb-4" style={{ maxWidth: 300 }}
        />
      )}

      {error && <Alert type="error" showIcon message={error} className="mb-3" />}

      {loading ? (
        <div className="py-10 text-center"><Spin /></div>
      ) : !buscado ? (
        <p className="py-10 text-center text-sm text-gray-500 m-0">Ingresa un RUT para buscar.</p>
      ) : items.length === 0 ? (
        <div className="py-10 text-center">
          <p className="font-bold text-gray-900 m-0">Sin inscripciones</p>
          <p className="text-sm text-gray-500 m-0 mt-1">Esta persona no tiene inscripciones, ni propias ni hechas por otra cuenta.</p>
        </div>
      ) : (
        <>
          <p className="text-sm text-gray-500 mb-3">
            {items.length} inscripcion{items.length !== 1 ? 'es' : ''} · {nVigentes} vigente{nVigentes !== 1 ? 's' : ''}
            <span className="text-gray-400"> — incluye las hechas para esta persona desde otra cuenta</span>
          </p>
          <ul className="m-0 p-0 list-none space-y-2.5">
            {visibles.map(row => {
              const est = estadoDe(row);
              const paraOtro = row.inscrito?.rut && rutInicial && rutLimpio(row.inscrito.rut) !== rutLimpio(rutInicial);
              const compradoPorOtro = usuarioId && row.usuario && row.usuario !== usuarioId;
              return (
                <li key={row.id} className={`p-4 rounded-2xl bg-white border ${est === ESTADOS.vigente ? 'border-blue-100' : 'border-gray-100'}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-bold text-gray-900 m-0 leading-snug">{row.taller || '—'}</p>
                      {row.horario && (
                        <p className="flex items-center gap-1.5 text-sm text-gray-600 m-0 mt-1 capitalize">
                          <FiClock size={13} className="text-primary flex-shrink-0" /> {row.horario}
                        </p>
                      )}
                    </div>
                    <span className={`flex-shrink-0 px-2.5 py-1 rounded-full text-xs font-bold ${est.cls}`}>{est.label}</span>
                  </div>

                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-3 text-xs text-gray-600">
                    {row.duracion?.fechaInicio && (
                      <span className="inline-flex items-center gap-1.5 tabular-nums">
                        <FiCalendar size={13} className="text-gray-400" />
                        {dayjs(row.duracion.fechaInicio).format('DD/MM/YY')} → {dayjs(row.duracion.fechaTermino).format('DD/MM/YY')}
                      </span>
                    )}
                    {row.plan?.nombre && (
                      <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 font-semibold capitalize">
                        {row.plan.nombre}{row.plan.tipoPrecio ? ` · ${row.plan.tipoPrecio}` : ''}
                      </span>
                    )}
                    {row.descuentos?.esVecino && (
                      <span className="px-2 py-0.5 rounded-full bg-secondary/40 text-[#5c4300] font-bold">Precio vecino</span>
                    )}
                    {paraOtro && (
                      <span className="inline-flex items-center gap-1.5">
                        <FiUser size={13} className="text-gray-400" /> Para {formatearNombre(row.inscrito.nombre)}
                        <span className="text-gray-400">({row.inscrito.rut})</span>
                      </span>
                    )}
                    {compradoPorOtro && (
                      <Tooltip title={`ID de la cuenta compradora: ${row.usuario}`}>
                        <span className="inline-flex items-center gap-1.5 cursor-help">
                          <FiShoppingBag size={13} className="text-gray-400" /> Comprada desde otra cuenta
                        </span>
                      </Tooltip>
                    )}
                  </div>

                  {/* Solo llega para quien puede cambiar inscripciones (la API lo filtra) */}
                  {row.cambiosTaller?.length > 0 && (
                    <ul className="m-0 mt-3 p-0 list-none space-y-1.5 border-t border-gray-100 pt-3">
                      {row.cambiosTaller.map((c, i) => (
                        <li key={c._id || i} className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-600">
                          <FiRepeat size={13} className="text-gray-400 flex-shrink-0" />
                          <span>
                            {dayjs(c.fecha).format('DD/MM/YY')} · desde <b>{c.tallerOrigenNombre || 'otro taller'}</b>
                            {' '}({pesos(c.montoPagado)} → {pesos(c.precioDestino)})
                          </span>
                          <span className={`px-2 py-0.5 rounded-full font-bold ${SALDO[c.sentido]?.cls || ''}`}>
                            {textoSaldo(c)}
                          </span>
                          {c.realizadoPorNombre && <span className="text-gray-400">por {c.realizadoPorNombre}</span>}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
          {items.length > POR_PAGINA && (
            <div className="flex justify-end mt-4">
              <Pagination size="small" current={pagina} pageSize={POR_PAGINA} total={items.length} onChange={setPagina} />
            </div>
          )}
        </>
      )}
    </div>
  );
}
